import { createContext, Dispatch, useContext } from "react";
import { StorefrontConfig } from "./storefront";
import { restaurantPath, storeCartPath, storeCategoriesPath, storeSearchPath } from "src/routes";

const NavigationContext = createContext<[NavigationState, Dispatch<NavigationAction>] | undefined>(undefined);

enum StorefrontPage {
  Shop = "shop",
  Categories = "categories",
  Search = "search",
  Cart = "cart",
}

class NavigationState {
  readonly currentPage: StorefrontPage;
  readonly searchQuery: string;
  readonly focusedCatalogComponent: readonly string[];
  readonly focusedProduct: readonly string[];
  readonly queryParams: Readonly<{ [param: string]: string }>;
  readonly queryId?: string;

  constructor(
    currentPage: StorefrontPage,
    searchQuery: string,
    focusedCatalogComponent: readonly string[],
    focusedProduct: readonly string[],
    queryParams: Readonly<{ [param: string]: string }>,
    queryId?: string,
  ) {
    this.currentPage = currentPage;
    this.searchQuery = searchQuery;
    this.focusedCatalogComponent = focusedCatalogComponent;
    this.focusedProduct = focusedProduct;
    this.queryParams = queryParams;
    this.queryId = queryId;
  }
}

abstract class AbstractNavigationAction {
  readonly updateUrl: boolean;

  constructor(updateUrl: boolean) {
    this.updateUrl = updateUrl;
  }
}

class NavigatePage extends AbstractNavigationAction {
  readonly kind = "navigate_page";
  readonly newPage: StorefrontPage;

  constructor(newPage: StorefrontPage, updateUrl: boolean) {
    super(updateUrl);
    this.newPage = newPage;
  }
  
  static action(newPage: StorefrontPage, updateUrl = true) {
    return new NavigatePage(newPage, updateUrl);
  }
}

class UpdateSearch extends AbstractNavigationAction {
  readonly kind = "update_search";
  readonly newQuery: string;

  constructor(newQuery: string, updateUrl: boolean) {
    super(updateUrl);
    this.newQuery = newQuery;
  }

  static action(newQuery: string, updateUrl = true) {
    return new UpdateSearch(newQuery, updateUrl);
  }
}

class NavigateCatalog extends AbstractNavigationAction {
  readonly kind = "navigate_catalog";
  readonly newPath: string[];

  constructor(newPath: string[], updateUrl: boolean) {
    super(updateUrl);
    this.newPath = newPath;
  }
  
  static action(newPath: string[], updateUrl = true) {
    return new NavigateCatalog(newPath, updateUrl);
  }
}

class FocusProduct extends AbstractNavigationAction {
  readonly kind = "focus_product";
  readonly newPath: string[];
  readonly queryId?: string;

  constructor(newPath: string[], updateUrl: boolean, queryId?: string) {
    super(updateUrl);
    this.newPath = newPath;
    this.queryId = queryId;
  }
  
  static action(newPath: string[], updateUrl = true, queryId?: string) {
    return new FocusProduct(newPath, updateUrl, queryId);
  }
}

export class SelectBreadcrumb extends AbstractNavigationAction {
  readonly kind = "select_breadcrumb";
  readonly newPath: string[];

  constructor(newPath: string[], updateUrl: boolean) {
    super(updateUrl);

    this.newPath = newPath;
  }

  static action(newPath: string[], updateUrl = true) {
    return new SelectBreadcrumb(newPath, updateUrl);
  }
}

type NavigationAction = NavigatePage | UpdateSearch | NavigateCatalog | FocusProduct | SelectBreadcrumb;

const serializeShopState = (state: NavigationState): string | undefined => {
  const hashState = {
    ...(state.focusedCatalogComponent.length > 0 && { fc: state.focusedCatalogComponent }),
    ...(state.focusedProduct.length > 0 && { p: state.focusedProduct }),
  }

  return Object.keys(hashState).length > 0 ? encodeURIComponent(JSON.stringify(hashState)) : undefined;
}

const deserializeShopState = (strState: string): [string[], string[]] => {
  try {
    const data = JSON.parse(decodeURIComponent(strState));
    return [data.fc || [], data.p || []];
  } catch (e) {
    if (e instanceof SyntaxError || e instanceof URIError) {
      return [[], []];
    }

    throw e;
  }
}

const buildUrlForState = (config: StorefrontConfig, newState: NavigationState) => {
  switch (newState.currentPage) {
    case StorefrontPage.Shop:
      return restaurantPath(config.storefrontPermalink, { anchor: serializeShopState(newState), params: newState.queryParams });

    case StorefrontPage.Categories:
      return storeCategoriesPath(config.storefrontPermalink, { anchor: serializeShopState(newState), params: newState.queryParams });

    case StorefrontPage.Search:
      return storeSearchPath(config.storefrontPermalink, { search_bar: newState.searchQuery, anchor: serializeShopState(newState), params: newState.queryParams });

    case StorefrontPage.Cart:
      return storeCartPath(config.storefrontPermalink, { params: newState.queryParams });
  }
}

const navigationReducerWithConfig = (config: StorefrontConfig): ((prev: NavigationState, action: NavigationAction) => NavigationState) => {
  return (prev: NavigationState, action: NavigationAction): NavigationState => navigationReducer(config, prev, action);
}

const navigationReducer = (config: StorefrontConfig, prev: NavigationState, action: NavigationAction): NavigationState => {
  switch (action.kind) {
    case "navigate_page": {
      if (action.newPage == prev.currentPage) return prev;

      const newState = new NavigationState(action.newPage, prev.searchQuery, prev.focusedCatalogComponent, prev.focusedProduct, prev.queryParams);
      document.querySelector("body")?.setAttribute("data-active-bottom-nav-view", action.newPage.toString());

      if (action.updateUrl)
        window.history.pushState(null, "", buildUrlForState(config, newState));

      return newState;
    }

    case "update_search": {
      if (prev.searchQuery === action.newQuery) {
        // Avoid no-op changes due to Algolia internal state
        // updates
        return prev;
      }

      const newState = new NavigationState(prev.currentPage, action.newQuery, prev.focusedCatalogComponent, prev.focusedProduct, prev.queryParams);

      if (action.updateUrl)
        window.history.replaceState(null, "", buildUrlForState(config, newState));

      return newState;
    }

    case "navigate_catalog": {
      if (JSON.stringify(action.newPath) === JSON.stringify(prev.focusedCatalogComponent)) return prev;

      const newState = new NavigationState(prev.currentPage, prev.searchQuery, action.newPath, prev.focusedProduct, prev.queryParams);

      if (action.updateUrl)
        window.history.pushState(null, "", buildUrlForState(config, newState));

      return newState;
    }

    case "focus_product": {
      const newState = new NavigationState(prev.currentPage, prev.searchQuery, prev.focusedCatalogComponent, action.newPath, prev.queryParams, action.queryId);

      if (!action.updateUrl) {
        return newState;
      }

      if (action.newPath.length > 0) {
        window.history.pushState(null, "", buildUrlForState(config, newState));
      } else {
        window.history.go(-1);
      }

      return newState;
    }

    case "select_breadcrumb": {
      const newState = new NavigationState(StorefrontPage.Shop, prev.searchQuery, action.newPath, [], prev.queryParams)
      document.querySelector("body")?.setAttribute("data-active-bottom-nav-view", StorefrontPage.Shop.toString());

      if (action.updateUrl) window.history.pushState(null, "", buildUrlForState(config, newState));

      return newState;
    }
  }
}

class MissingNavigationState extends Error {}
const useNavigationState = (): [NavigationState, Dispatch<NavigationAction>] => {
  const maybeNavigationState = useContext(NavigationContext);

  if (!maybeNavigationState)
    throw new MissingNavigationState();

  return maybeNavigationState;
}

export {
  NavigationContext, NavigationState, StorefrontPage, useNavigationState,
  navigationReducer, navigationReducerWithConfig,
  NavigationAction, NavigatePage, UpdateSearch, NavigateCatalog, FocusProduct,
  deserializeShopState, buildUrlForState, serializeShopState,
}
